import React from 'react';
import StackedBarChart from './StackedBarChart';
import BarChart from './BarChart';
import PieChart from './PieChart';
import HeatmapChart from './HeatmapChart';
import MatrixChart from './MatrixChart';

interface StackedData {
  label: string;
  value: number;
  color: string;
}

interface VisualizationData {
  category?: string;
  name?: string;
  value: string;
  percentage?: string;
  breakdown?: string;
  description?: string;
  ranking?: number;
  score?: number;
  stacked_data?: StackedData[];
}

interface Visualization {
  type: 'bar_chart' | 'pie_chart' | 'table' | 'heatmap' | 'matrix';
  title: string;
  data: VisualizationData[];
}

interface VisualizationRendererProps {
  visualization: Visualization;
  questionType?: string;
  className?: string;
  optionImages?: Record<string, string>;
  imageAttachmentLevel?: 'question' | 'option';
  isExporting?: boolean;
}

const VisualizationRenderer: React.FC<VisualizationRendererProps> = ({
  visualization,
  questionType = 'default',
  className = '',
  optionImages,
  imageAttachmentLevel,
  isExporting = false,
}) => {
  if (!visualization || !visualization.data) return null;

  const commonProps = {
    visualization,
    questionType,
    className,
    optionImages,
    imageAttachmentLevel,
    isExporting,
  };

  switch (visualization.type) {
    case 'pie_chart':
      return <PieChart {...commonProps} visualization={{ ...visualization, type: 'pie_chart' }} />;
    case 'heatmap':
      return (
        <HeatmapChart {...commonProps} visualization={{ ...visualization, type: 'heatmap' }} />
      );
    case 'matrix':
      return <MatrixChart {...commonProps} visualization={{ ...visualization, type: 'matrix' }} />;
    case 'bar_chart':
    default:
      // Check if this is a stacked chart (ranking questions with position breakdown)
      if (visualization.data && visualization.data[0]?.stacked_data) {
        return (
          <StackedBarChart
            {...commonProps}
            visualization={{ ...visualization, type: 'bar_chart' }}
          />
        );
      }
      return <BarChart {...commonProps} visualization={{ ...visualization, type: 'bar_chart' }} />;
  }
};

export default VisualizationRenderer;
